import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { PRICE_MODES } from "../constants/priceModes";
import { formatToCurrency, calculatePercentageAmount } from "../utils/numbers";
import { CURRENCY } from "../constants/common";
import {
  getShapesFromComposition,
  getMaterialsFromComposition,
  parseComposition,
} from "../utils/designs";
import LayoutSize from "../models/furniture/LayoutSize";
import LayoutBody from "../models/furniture/LayoutBody";
import Shape from "../models/furniture/Shape";
import Material from "../models/furniture/Material";

export interface DesignState {
  id: number | null;
  composition: string | null;
  layout_size: LayoutSize | null;
  layout_body: LayoutBody | null;
  shapes: Record<string, Shape>; // elementOrder -> Shape
  materials: Record<string, Material>; // "elementOrder_materialOrder" -> Material
  prices: Array<[string, string | null, string]>;
  price: number;
  priceRangePercentage: number;
  pricePerStep: number[];
  priceRangePercentagePerStep: number[];
  isChanged: boolean;
  isLastChangeMaterial: boolean;
  image: string | null; // storage key of the Custom Shop View image, saved with the design
  imageUrl: string | null; // its URL, from the API
  lastDisplayedImage: string | null;
  priceMode: string;
  lastDimensionFilter: string | null;
}

const initialState: DesignState = {
  id: null,
  composition: null,
  layout_size: null,
  layout_body: null,
  shapes: {},
  materials: {},
  prices: [],
  price: 0,
  priceRangePercentage: 0,
  pricePerStep: [],
  priceRangePercentagePerStep: [],
  isChanged: false,
  isLastChangeMaterial: false,
  image: null,
  imageUrl: null,
  lastDisplayedImage: null,
  priceMode: PRICE_MODES.ON,
  lastDimensionFilter: null,
};

const designSlice = createSlice({
  name: "design",
  initialState,
  reducers: {
    init: (
      state,
      action: PayloadAction<{
        id: number;
        composition: string | null;
        layout_size?: LayoutSize | null;
        layout_body?: LayoutBody | null;
        shapes?: Record<number, Shape> | null;
        materials?: Record<string, Material> | null;
        image?: string | null;
        image_url?: string | null;
        price_mode?: string;
      }>
    ) => {
      const {
        id,
        composition,
        layout_size = null,
        layout_body = null,
        shapes = {},
        materials = {},
        image,
        image_url,
        price_mode = PRICE_MODES.ON,
      } = action.payload;

      state.id = id;
      state.composition = composition;
      state.layout_size = layout_size;
      state.layout_body = layout_body;
      state.shapes = shapes || {};
      state.materials = materials || {};
      state.isChanged = false;
      state.isLastChangeMaterial = false;
      state.image = image || null;
      state.imageUrl = image_url || null;
      state.priceMode = price_mode;

      recalculatePrices(state);
    },

    reset: () => initialState,

    setComposition: (
      state,
      action: PayloadAction<{
        composition: string | null;
        shapes: Record<number, Shape> | null;
        materials: Record<string, Material> | null;
      }>
    ) => {
      // if (!action.payload.composition || action.payload.composition === state.composition) {
      //   return;
      // }

      state.composition = action.payload.composition;
      state.shapes = action.payload.shapes || {};
      state.materials = action.payload.materials || {};

      // state.isChanged = true;
      recalculatePrices(state);
    },

    setImage: (
      state,
      action: PayloadAction<{ image: string; imageUrl: string | null }>
    ) => {
      state.image = action.payload.image;
      state.imageUrl = action.payload.imageUrl;
    },

    updateCompositionWithLayout: (
      state,
      action: PayloadAction<{
        layoutSize?: LayoutSize;
        layoutBody?: LayoutBody;
      }>
    ) => {
      const { layoutSize, layoutBody } = action.payload;

      if (!layoutSize && !layoutBody) {
        return;
      }

      // const layoutSizeCode = layoutSize
      //   ? layoutSize.code.substring(0, 3)
      //   : state.layout_size?.code.substring(0, 3) || "";
      // const layoutBodyCode = layoutBody
      //   ? layoutBody.code.substring(0, 3)
      //   : state.layout_body?.code.substring(0, 3) || "";

      // state.composition = `(${layoutSizeCode}${layoutBodyCode})`;

      if (layoutSize) {
        state.layout_size = layoutSize;
      }
      if (layoutBody) {
        state.layout_body = layoutBody;
      }

      state.composition = null;
      state.shapes = {};
      state.materials = {};

      state.isChanged = true;
      state.isLastChangeMaterial = false;

      recalculatePrices(state);
    },

    updateCompositionWithShape: (
      state,
      action: PayloadAction<{
        elementOrder: number;
        shape: Shape;
      }>
    ) => {
      const { elementOrder, shape } = action.payload;

      const newComposition = updateShapeInComposition(
        state.composition,
        elementOrder,
        shape.code
      );

      state.composition = newComposition;

      state.shapes[elementOrder] = shape;

      Object.keys(state.materials).forEach((materialKey) => {
        const [matElementOrder] = materialKey.split("_");
        if (+matElementOrder === +elementOrder) {
          delete state.materials[materialKey];
        }
      });

      state.isChanged = true;
      state.isLastChangeMaterial = false;

      recalculatePrices(state);
    },

    updateCompositionWithMaterial: (
      state,
      action: PayloadAction<{
        elementOrder: number;
        materialOrder: number;
        material: Material;
      }>
    ) => {
      const { elementOrder, materialOrder, material } = action.payload;

      const newComposition = updateMaterialInComposition(
        state.composition,
        elementOrder,
        materialOrder,
        material.code
      );

      state.composition = newComposition;

      const materialKey = `${elementOrder}_${materialOrder}`;
      state.materials[materialKey] = material;

      state.isChanged = true;
      state.isLastChangeMaterial = true;

      recalculatePrices(state);
    },

    clearElementsFromComposition: (state) => {
      state.composition = "()()";

      state.layout_size = null;
      state.layout_body = null;
      state.shapes = {};
      state.materials = {};

      state.isChanged = true;
      state.isLastChangeMaterial = false;

      recalculatePrices(state);
    },

    setLastDisplayedImage: (state, action: PayloadAction<string | null>) => {
      state.lastDisplayedImage = action.payload;
    },

    setLastDimensionFilter: (
      state,
      action: PayloadAction<{ newDimensionFilter: string | null }>
    ) => {
      state.lastDimensionFilter = action.payload.newDimensionFilter;
    },

    markAsChanged: (state) => {
      state.isChanged = true;
    },

    markAsUnchanged: (state) => {
      state.isChanged = false;
    },
  },
});

function updateShapeInComposition(
  currentComposition: string | null,
  elementOrder: number,
  newShapeCode: string
): string {
  let shapeCodes: string[] = [];
  let materialGroups: string[][] = [];

  if (currentComposition) {
    const { shapes, materials } = parseComposition(currentComposition);
    shapeCodes = shapes;
    materialGroups = materials;
  }

  const shapeIdx = elementOrder - 1;

  while (shapeCodes.length <= shapeIdx) {
    shapeCodes.push("");
  }

  shapeCodes[shapeIdx] = newShapeCode;

  while (materialGroups.length <= shapeIdx) {
    materialGroups.push([]);
  }

  const shapesPart = `(${shapeCodes.join(",")})`;
  const materialsPart = `(${materialGroups
    .map((group) => `[${group.join(", ")}]`)
    .join(",")})`;

  return `${shapesPart}${materialsPart}`;
}

function updateMaterialInComposition(
  currentComposition: string | null,
  elementOrder: number,
  materialOrder: number,
  newMaterialCode: string
): string {
  let shapeCodes: string[] = [];
  let materialGroups: string[][] = [];

  if (currentComposition) {
    const { shapes, materials } = parseComposition(currentComposition);
    shapeCodes = shapes;
    materialGroups = materials;
  }

  const shapeIdx = elementOrder - 1;
  const materialIdx = materialOrder - 1;

  while (materialGroups.length <= shapeIdx) {
    materialGroups.push([]);
  }

  while (materialGroups[shapeIdx].length <= materialIdx) {
    materialGroups[shapeIdx].push("");
  }

  materialGroups[shapeIdx][materialIdx] = newMaterialCode;

  const shapesPart = `(${shapeCodes.join(",")})`;
  const materialsPart = `(${materialGroups
    .map((group) => `[${group.join(",")}]`)
    .join(",")})`;

  return `${shapesPart}${materialsPart}`;
}

function recalculatePrices(state: DesignState) {
  state.price = 0;
  state.prices = [];
  state.pricePerStep = [];
  state.priceRangePercentagePerStep = [];
  let totalPriceR = 0;

  if (state.layout_size) {
    const { name, price, dimensions, range_price_percentage } =
      state.layout_size;

    state.price += +(price || 0);
    totalPriceR += calculatePercentageAmount(
      +(price || 0),
      +range_price_percentage
    );

    state.prices.push([
      name,
      dimensions || null,
      formatToCurrency(
        price || 0,
        CURRENCY,
        +range_price_percentage || 0,
        state.priceMode
      ) || "",
    ]);
    state.pricePerStep.push(state.price);
    state.priceRangePercentagePerStep.push(
      state.price > 0 ? ((totalPriceR - state.price) / state.price) * 100 : 0
    );
  }

  if (state.layout_body) {
    const { name, price, dimensions, range_price_percentage } =
      state.layout_body;

    state.price += +price;
    totalPriceR += calculatePercentageAmount(+price, +range_price_percentage);

    state.prices.push([
      name,
      dimensions || null,
      formatToCurrency(
        price || 0,
        CURRENCY,
        +range_price_percentage,
        state.priceMode
      ) || "",
    ]);
    state.pricePerStep.push(state.price);
    state.priceRangePercentagePerStep.push(
      state.price > 0 ? ((totalPriceR - state.price) / state.price) * 100 : 0
    );
  }

  Object.values(state.shapes).forEach((shape) => {
    const { name, price, dimensions, range_price_percentage } = shape;
    state.price += +price;
    totalPriceR += calculatePercentageAmount(+price, +range_price_percentage);
    state.prices.push([
      name,
      dimensions || null,
      formatToCurrency(
        price || 0,
        CURRENCY,
        +range_price_percentage,
        state.priceMode
      ) || "",
    ]);
    state.pricePerStep.push(state.price);
    state.priceRangePercentagePerStep.push(
      state.price > 0 ? ((totalPriceR - state.price) / state.price) * 100 : 0
    );
  });

  Object.entries(state.materials).forEach(([materialKey, material]) => {
    const { name, price, range_price_percentage } = material;
    const [elementOrder] = materialKey.split("_");
    const associatedShape = state.shapes[elementOrder];

    state.price += +price;
    totalPriceR += calculatePercentageAmount(+price, +range_price_percentage);

    const displayName = associatedShape
      ? `${associatedShape.name} - ${name}`
      : name;

    state.prices.push([
      displayName,
      null,
      formatToCurrency(
        price || 0,
        CURRENCY,
        +range_price_percentage,
        state.priceMode
      ) || "",
    ]);
    state.pricePerStep.push(state.price);
    state.priceRangePercentagePerStep.push(
      state.price > 0 ? ((totalPriceR - state.price) / state.price) * 100 : 0
    );
  });

  state.priceRangePercentage =
    state.price > 0 ? ((totalPriceR - state.price) / state.price) * 100 : 0;
}

export const selectShapeCodes = (state: { design: DesignState }) =>
  getShapesFromComposition(state.design.composition);

export const selectMaterialCodes = (state: { design: DesignState }) =>
  getMaterialsFromComposition(state.design.composition);

export const {
  init,
  reset,
  setComposition,
  setImage,
  updateCompositionWithLayout,
  updateCompositionWithShape,
  updateCompositionWithMaterial,
  clearElementsFromComposition,
  setLastDisplayedImage,
  setLastDimensionFilter,
  markAsChanged,
  markAsUnchanged,
} = designSlice.actions;

export default designSlice.reducer;
